import { FC, useEffect, useRef } from 'react';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { pushEvent } from 'src/utils/event-gateway';
import useReady from './useReady';
declare global {
  interface Window {
    /** _document.tsx의 splashShownScript가 기록하는, 스플래시가 실제로 그려진 시각(performance.now() 기준) */
    __splashShownAt?: number;
  }
}

/** 페이드아웃 애니메이션 지속 시간 (_document.tsx의 splash-fade-out과 일치) */
const FADE_OUT_DURATION_MS = 400;

/**
 * _document.tsx에서 인라인으로 렌더된 스플래시(#splash-screen)를 제거하는 컴포넌트.
 * useReady()가 완료되면 페이드아웃 클래스 추가 → 400ms 후 DOM 제거.
 */
const SplashScreen: FC = () => {
  const {
    ready
  } = useReady();
  const pendingRemovalTimer = useRef<number>();

  // ErrorBoundary가 ready:true가 되기 전에 트리를 교체해 이 컴포넌트가 조기 unmount되는 경우를 대비해,
  // ready 상태와 무관하게 언마운트 시 스플래시를 제거한다.
  // reactStrictMode의 dev 합성 마운트→언마운트→재마운트에서 즉시 제거되지 않도록,
  // 제거를 한 틱 지연시키고 재마운트되면 취소한다 (실제 unmount일 때만 최종 실행됨).
  useEffect(() => {
    window.clearTimeout(pendingRemovalTimer.current);
    return () => {
      pendingRemovalTimer.current = window.setTimeout(() => {
        document.getElementById('splash-screen')?.remove();
      }, 0);
    };
  }, []);

  // GTM에는 _document.tsx의 인라인 스크립트가 하이드레이션 전에 이미 보냈으므로,
  // 여기서는 이벤트로그에만 적재한다 (중복 전송 방지).
  // skipSplash 라우트는 __splashShownAt이 설정되지 않으므로(스플래시 자체가 렌더 안 됨) 건너뛴다.
  useEffect(() => {
    if (window.__splashShownAt == null) return;
    pushEvent({
      eventType: EVENT_TYPE.DEBUG,
      eventName: EVENT_NAME.DEBUG__SPLASH_SHOWN,
      eventParams: {
        time_ms: window.__splashShownAt
      },
      sendToDataLayer: false
    });
  }, []);
  useEffect(() => {
    if (!ready) return;
    const el = document.getElementById('splash-screen');
    if (!el) return;
    const shownAt = window.__splashShownAt ?? 0;
    pushEvent({
      eventType: EVENT_TYPE.DEBUG,
      eventName: EVENT_NAME.DEBUG__SPLASH_DISMISSED,
      eventParams: {
        duration_ms: Math.round(performance.now() - shownAt)
      }
    });
    el.classList.add('splash-fade-out');
    const timer = window.setTimeout(() => {
      el.remove();
    }, FADE_OUT_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [ready]);
  return null;
};
export default SplashScreen;